import Vue, { CreateElement } from '@src/vue'
import { Component, Prop } from 'vue-property-decorator'
import { Body2, H6, Overline, Subtitle2 } from '@src/elements/typography/typography'
import { v1 as uuidv1 } from 'uuid'
import { Card } from '../../card'
import { Icon } from '@src/elements/icon/icon'
import { WorkoutExercise } from '@src/data/workout-exercise'
import { ExerciseResponse } from '@src/data/exercise-response'
import { ExercisePreview } from '../exercise-preview/exercise-preview'
import { Tooltip } from '@src/elements/tooltip/tooltip'
import { ExerciseInfoModal } from '@src/components/exercise-info/exercise-info'
import type { ExerciseTagFilter } from '@src/state/store'

import '../exercise-card.sass'
import SwitchButton from '@src/components/switch-button/switch-button'
import SessionManager from '@src/lib/session_manager'
import { exerciseName } from '@src/util/exercise-name'

@Component({
  computed: {
    selectedExercises () {
      return this.$store.state.new_workout.workoutExercises
    }
  }
})
export class ExerciseCardSelect extends Vue {

  @Prop({ required: true })
  exercise: ExerciseResponse

  @Prop({ required: false, default: false })
  hidePreview: boolean

  @Prop({ required: false, default: false })
  hideDetails: boolean

  @Prop({
    required: false, default: () => null
  })
  selectedFilters: ExerciseTagFilter

  @Prop({ required: true })
  exerciseCounter: number

  @Prop({ required: true })
  type: 'add' | 'select'

  selectedExercises: WorkoutExercise[]

  private showExerciseInfo: boolean = false
  private addTooltip = true

  constructor (props) {
    super(props)
  }

  updated () {
    this.checkTitle()
  }

  mounted () {
    this.checkTitle()
  }

  // check if title doesn't fit
  checkTitle () {
    let title = this.$refs.title as Vue
    this.addTooltip = title.$el.clientWidth < title.$el.scrollWidth
  }

  render (h: CreateElement) {
    let selected = false
    if (this.type === 'select') {
      if (this.selectedExercises.length === 1 && this.selectedExercises[0].exerciseId === this.exercise.id) {
        selected = true
      }
    }

    return <Card class={`exercise-card exercise-card-select ${selected ? 'selected' : ''} ${this.type}`} draggable={this.type === 'add' ? true : false} click={this.selectExercise}>
      {this.renderHeader(h)}
      {!this.hidePreview && this.renderPreview(h)}
      {this.renderTitle(h)}
      {!this.hideDetails && this.renderSpecs(h)}
      {this.renderRight(h, selected)}
    </Card>
  }

  renderHeader (h: CreateElement) {
    return <Overline slot='header' class='header'>
      <Overline>{this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.PHASE_PLACEHOLDER')} {this.exercise.complexityLevel} /&nbsp;</Overline>
      <Overline>
        {this.exercise.is2DTracked() && this.exercise.is3DTracked() ? this.$t('PAGE.CONTENT.WORKOUTS.TRACKED')
          : this.exercise.is2DTracked() ? this.$t('PAGE.CONTENT.EXERCISES.2D_TRACKED')
            : this.exercise.is3DTracked() ? this.$t('PAGE.CONTENT.EXERCISES.3D_TRACKED')
              : this.$t('PAGE.CONTENT.WORKOUTS.NON_TRACKED')} /&nbsp;</Overline>
      <Overline>ID {this.exercise.number}</Overline>
      {this.exercise.equipment.length > 0 &&
        <div>
          &nbsp;/&nbsp;
          {this.renderWeight(h)}
        </div>
      }
    </Overline>
  }

  renderPreview (h: CreateElement) {
    return <div slot='preview'>
      <ExercisePreview exercise={this.exercise} />
    </div>
  }

  renderTitle (h: CreateElement) {
    const title = exerciseName(this, this.exercise)
    return this.addTooltip ?
      <Tooltip slot='title' content={title}>
        <H6 class='title' ref='title'>{title}</H6>
      </Tooltip>
      : <H6 class='title' slot='title' ref='title'>{title}
      </H6>
  }

  renderSpecs (h: CreateElement) {
    return <div slot='specs'>
      <div class='side-wrapper'>
        {this.renderSide(h)}
      </div>
      <div class='tags-wrapper'>
        {this.renderTags(h)}
      </div>
    </div>
  }

  renderTags (h: CreateElement) {
    const tags = this.exercise.getTagKeysWOHealthLimitations()
      // show the filter tags first
      .sort((a, b) => this.isTagSelected(a) ? -1 : 1)
      .map(tag => {
        return <span class={this.isTagSelected(tag) ? 'selected tag' : 'tag'}><Overline>{this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.' + tag.toUpperCase())}</Overline></span>
      })
    return <div slot='specs' class='specs'>
      {tags}
    </div>
  }
  renderWeight (h: CreateElement) {
    const allEquipmentItems = this.exercise.equipment.map((equipmentName) => {
      return this.renderEquipment(h, equipmentName)
    })
    return <div slot='specs' class='specs-equipment'>
      <div class='equipmentWrapper'>{allEquipmentItems}</div>
    </div>
  }

  renderEquipment (h: CreateElement, equipment: string) {
    return <div key={uuidv1()} class='equipment'>
      <Tooltip content={this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.' + equipment.toUpperCase())}>
        <Icon name={'equipment/' + equipment} style={{ padding: 0, marginRight: 10, marginBottom: 10 }}/>
      </Tooltip>
    </div>
  }

  renderSide (h: CreateElement) {
    if (this.exercise.hasSides) {
      return <span ref='exercise-details-side'
                   class={`exercise-details`}>
    <Subtitle2>{this.$t('BODY_SIDE.SIDE') + ': '}
      <b>{this.exercise.side ? this.$t(`BODY_SIDE.${this.exercise.side.toUpperCase()}`) : this.$t('BODY_SIDE.ALTERNATING')}</b></Subtitle2>
    </span>
    }
  }

  renderRight (h: CreateElement, selected: Boolean) {
    return <div slot='right'>
      {this.type === 'add' ?
        this.renderRightAdd(h) :
        this.renderRightSelect(h, selected)
      }
      {SessionManager.Instance.notMemberOrAssistant() &&
        <Tooltip content={this.exercise.isFavorite
          ? this.$t('PAGE.CONTENT.EXERCISES.REMOVE_FROM_FAVORITES')
          : this.$t('PAGE.CONTENT.EXERCISES.ADD_TO_FAVORITES')}>
          <SwitchButton
            class={'favourites-button'}
            selected={this.favouriteExercise}
            unSelected={this.unfavouriteExercise}
            defaultSelected={this.exercise.isFavorite}
            selectedIcon={<Icon name={'util/starRed-filled'} />}
            unSelectedIcon={<Icon name={'util/starRed'} />}
          />
        </Tooltip>
      }
    </div>
  }

  renderRightAdd (h: CreateElement) {
    return <div class='right'>
    <div class='row-btns'>
      <Tooltip content={this.$t('PAGE.CONTENT.WORKOUT.DRAG_TO_ADD_TO_WORKOUT')}><Icon class='drag-btn' name='/util/drag' /></Tooltip>
      {this.exerciseCounter === 0 ?
        <Tooltip content={this.$t('PAGE.CONTENT.WORKOUT.ADD_TO_WORKOUT')}><Icon class='add-btn' name='/util/add-circle-outline' click={this.addExercise} /></Tooltip>
        : <div class='add-more-btn' onClick={this.addExercise}>
            <span class='counter'>
              <Body2>{this.exerciseCounter}</Body2>
              </span>
          <Tooltip content={this.$t('PAGE.CONTENT.WORKOUT.ADD_TO_WORKOUT')}><Icon class='add-btn' name='/util/add-circle' /></Tooltip>
        </div>
      }
      <Tooltip content={this.$t('VIEW_DETAILS')}><Icon name='/util/info' class='info-btn' click={this.showHideInfo} /></Tooltip>
      {this.showExerciseInfo ?
        <ExerciseInfoModal exercise={this.exercise}
                           showModal={this.showExerciseInfo}
                           closeCallback={this.showHideInfo} /> : null}

      </div>
    </div>
  }

  renderRightSelect (h: CreateElement, selected: Boolean) {
    return selected ? <Icon class='selected-btn' name='/util/selected' /> : ''
  }

  favouriteExercise () {
    this.$store.dispatch('favouriteExercise', this.exercise)
    this.$emit('exercise-favourited', this.exercise)
  }

  unfavouriteExercise () {
    this.$store.dispatch('unfavouriteExercise', this.exercise)
    this.$emit('exercise-unfavourited', this.exercise)
  }

  isTagSelected (tagKey) {
    if (!this.selectedFilters) {
      return false
    }
    for (let type in this.selectedFilters) {
      if (this.selectedFilters[type].has(tagKey)) return true
    }
    return false
  }

  addExercise () {
    this.$store.dispatch('addExerciseToWorkout', { exercise: this.exercise })
  }

  selectExercise () {
    if (this.type === 'select') {
      this.$store.dispatch('addSingleExerciseToWorkout', { exercise: this.exercise })
    }
  }
  showHideInfo () {
    this.showExerciseInfo = !this.showExerciseInfo
  }
}
